import { create } from 'zustand'
import { io, Socket } from 'socket.io-client'
import { api } from '../services/api'

// Agent types
export type AgentRole = 'architect' | 'developer' | 'qa'
export type AgentStatus = 'idle' | 'thinking' | 'working' | 'reviewing' | 'completed' | 'error'
export type MultiAgentStatus = 
  | 'idle'
  | 'architect_planning'
  | 'awaiting_plan_approval'
  | 'developer_implementing'
  | 'qa_reviewing'
  | 'awaiting_final_approval'
  | 'executing'
  | 'completed'
  | 'cancelled'
  | 'error'

export interface AgentMessage {
  id: string
  agent: AgentRole
  agentName: string
  content: string
  timestamp: string
  attachments?: string[]
}

export interface AgentInfo {
  role: AgentRole
  name: string
  status: AgentStatus
  lastActivity?: string
  taskCount: number
  completedTasks: number
}

export interface ArchitecturePlan {
  id: string
  overview: string
  fileStructure: Array<{
    path: string
    action: 'create' | 'modify' | 'delete'
    description: string
    content?: string
  }>
  dependencies: Array<{
    from: string
    to: string
    type: 'import' | 'function_call' | 'data_flow'
    description: string
  }>
  tasks: Array<{
    id: string
    title: string
    description: string
    assignee: AgentRole
    priority: 'high' | 'medium' | 'low'
    estimatedTime: string
    dependencies: string[]
  }>
  risks: Array<{
    level: 'high' | 'medium' | 'low'
    description: string
    mitigation: string
  }>
  estimatedTime: string
  confidence: number
}

export interface MultiAgentSessionData {
  id: string
  userId: string
  vfsId: string
  codebaseId?: string
  userRequest: string
  capabilities: string[]
  status: MultiAgentStatus
  currentPhase: string
  agents?: Record<AgentRole, AgentInfo>
  architecturePlan?: ArchitecturePlan
  implementationProgress: number
  messages: AgentMessage[]
  filesCreated: number
  filesModified: number
  filesDeleted: number
  summary?: string
  approvedBy?: string
  approvedAt?: string
  error?: string
  createdAt: string
  updatedAt: string
  completedAt?: string
}

interface MultiAgentStore {
  // State
  sessions: MultiAgentSessionData[]
  currentSession: MultiAgentSessionData | null
  loading: boolean
  error: string | null
  socket: Socket | null
  
  // Actions
  connectSocket: () => void
  disconnectSocket: () => void
  fetchSessions: () => Promise<void>
  fetchSession: (sessionId: string) => Promise<void>
  createSession: (userRequest: string, vfsId: string, capabilities?: string[], codebaseId?: string) => Promise<MultiAgentSessionData | null>
  runArchitectPhase: (sessionId: string) => Promise<void>
  approvePlan: (sessionId: string) => Promise<void>
  runDeveloperPhase: (sessionId: string) => Promise<void>
  runQAPhase: (sessionId: string) => Promise<void>
  approveFinal: (sessionId: string) => Promise<void>
  rejectSession: (sessionId: string, reason: string) => Promise<void>
  cancelSession: (sessionId: string) => Promise<void>
  deleteSession: (sessionId: string) => Promise<void>
  clearCurrentSession: () => void
  clearError: () => void
  updateSessionFromSocket: (session: MultiAgentSessionData) => void
}

const SOCKET_URL = import.meta.env?.VITE_WS_URL || ''

// Multi-Agent API
const multiAgentApi = {
  getSessions: () => api.get<{ sessions: MultiAgentSessionData[] }>('/multi-agent/sessions'),
  
  getSession: (id: string) => api.get<{ session: MultiAgentSessionData }>(`/multi-agent/sessions/${id}`),
  
  createSession: (data: { userRequest: string; vfsId: string; capabilities?: string[]; codebaseId?: string }) =>
    api.post<{ session: MultiAgentSessionData }>('/multi-agent', data),
  
  runArchitect: (sessionId: string) =>
    api.post<{ session: MultiAgentSessionData }>(`/multi-agent/sessions/${sessionId}/architect`),
  
  approvePlan: (sessionId: string) =>
    api.post<{ session: MultiAgentSessionData }>(`/multi-agent/sessions/${sessionId}/approve-plan`),
  
  runDeveloper: (sessionId: string) =>
    api.post<{ session: MultiAgentSessionData }>(`/multi-agent/sessions/${sessionId}/developer`),
  
  runQA: (sessionId: string) =>
    api.post<{ session: MultiAgentSessionData }>(`/multi-agent/sessions/${sessionId}/qa`),
  
  approveFinal: (sessionId: string) =>
    api.post<{ session: MultiAgentSessionData }>(`/multi-agent/sessions/${sessionId}/approve-final`),
  
  reject: (sessionId: string, reason: string) =>
    api.post<{ session: MultiAgentSessionData }>(`/multi-agent/sessions/${sessionId}/reject`, { reason }),
  
  cancel: (sessionId: string) =>
    api.post<{ session: MultiAgentSessionData }>(`/multi-agent/sessions/${sessionId}/cancel`),
  
  delete: (sessionId: string) =>
    api.delete(`/multi-agent/sessions/${sessionId}`),
}

export const useMultiAgentStore = create<MultiAgentStore>((set, get) => ({
  // Initial state
  sessions: [],
  currentSession: null,
  loading: false,
  error: null,
  socket: null,

  connectSocket: () => {
    const { socket } = get()
    if (socket?.connected) return
    
    const newSocket = io(SOCKET_URL, {
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    })

    newSocket.on('connect', () => {
      console.log('[MultiAgentStore] WebSocket connected')
    })

    newSocket.on('disconnect', () => {
      console.log('[MultiAgentStore] WebSocket disconnected')
    })

    newSocket.on('agent-update', (data: { sessionId: string; session: MultiAgentSessionData }) => {
      console.log('[MultiAgentStore] Received agent update:', data)
      get().updateSessionFromSocket(data.session)
    })

    newSocket.on('error', (error: string) => {
      console.error('[MultiAgentStore] WebSocket error:', error)
      set({ error })
    })

    set({ socket: newSocket })
  },

  disconnectSocket: () => {
    const { socket } = get()
    if (socket) {
      socket.disconnect()
      set({ socket: null })
    }
  },

  fetchSessions: async () => {
    set({ loading: true, error: null })
    try {
      const response = await multiAgentApi.getSessions()
      set({ sessions: response.data.sessions || [], loading: false })
    } catch (error: any) {
      console.error('Failed to fetch sessions:', error)
      set({ error: error.response?.data?.error || 'Failed to fetch sessions', loading: false })
    }
  },

  fetchSession: async (sessionId: string) => {
    set({ loading: true, error: null })
    try {
      const response = await multiAgentApi.getSession(sessionId)
      set({ currentSession: response.data.session, loading: false })
    } catch (error: any) {
      console.error('Failed to fetch session:', error)
      set({ error: error.response?.data?.error || 'Failed to fetch session', loading: false })
    }
  },

  createSession: async (userRequest: string, vfsId: string, capabilities?: string[], codebaseId?: string) => {
    set({ loading: true, error: null })
    try {
      const response = await multiAgentApi.createSession({
        userRequest,
        vfsId,
        capabilities: capabilities || ['read', 'write', 'delete', 'execute'],
        codebaseId,
      })
      const session = response.data.session
      console.log('[createSession] Session created:', session)
      console.log('[createSession] Session created:', session)
      
      set((state) => {
        console.log('[createSession] Updating state, current sessions:', state.sessions.length)
        return {
          sessions: [session, ...state.sessions],
          currentSession: session,
          loading: false,
        }
      })
      
      // Connect to socket if not connected
      const { socket } = get()
      if (!socket?.connected) {
        get().connectSocket()
      }
      
      return session
    } catch (error: any) {
      console.error('Failed to create session:', error)
      set({ error: error.response?.data?.error || 'Failed to create session', loading: false })
      return null
    }
  },

  runArchitectPhase: async (sessionId: string) => {
    set({ loading: true, error: null })
    try {
      const response = await multiAgentApi.runArchitect(sessionId)
      const session = response.data.session
      set((state) => ({
        currentSession: session,
        sessions: state.sessions.map(s => s.id === sessionId ? session : s),
        loading: false
      }))
    } catch (error: any) {
      console.error('Failed to run architect phase:', error)
      set({ error: error.response?.data?.error || 'Failed to run architect phase', loading: false })
    }
  },

  approvePlan: async (sessionId: string) => {
    set({ loading: true, error: null })
    try {
      const response = await multiAgentApi.approvePlan(sessionId)
      const session = response.data.session
      set((state) => ({
        currentSession: session,
        sessions: state.sessions.map(s => s.id === sessionId ? session : s),
        loading: false
      }))
    } catch (error: any) {
      console.error('Failed to approve plan:', error)
      set({ error: error.response?.data?.error || 'Failed to approve plan', loading: false })
    }
  },

  runDeveloperPhase: async (sessionId: string) => {
    set({ loading: true, error: null })
    try {
      const response = await multiAgentApi.runDeveloper(sessionId)
      const session = response.data.session
      set((state) => ({
        currentSession: session,
        sessions: state.sessions.map(s => s.id === sessionId ? session : s),
        loading: false
      }))
    } catch (error: any) {
      console.error('Failed to run developer phase:', error)
      set({ error: error.response?.data?.error || 'Failed to run developer phase', loading: false })
    }
  },

  runQAPhase: async (sessionId: string) => {
    set({ loading: true, error: null })
    try {
      const response = await multiAgentApi.runQA(sessionId)
      const session = response.data.session
      set((state) => ({
        currentSession: session,
        sessions: state.sessions.map(s => s.id === sessionId ? session : s),
        loading: false
      }))
    } catch (error: any) {
      console.error('Failed to run QA phase:', error)
      set({ error: error.response?.data?.error || 'Failed to run QA phase', loading: false })
    }
  },

  approveFinal: async (sessionId: string) => {
    set({ loading: true, error: null })
    try {
      const response = await multiAgentApi.approveFinal(sessionId)
      const session = response.data.session
      set((state) => ({
        currentSession: session,
        sessions: state.sessions.map(s => s.id === sessionId ? session : s),
        loading: false
      }))
    } catch (error: any) {
      console.error('Failed to approve final:', error)
      set({ error: error.response?.data?.error || 'Failed to approve final', loading: false })
    }
  },

  rejectSession: async (sessionId: string, reason: string) => {
    set({ loading: true, error: null })
    try {
      const response = await multiAgentApi.reject(sessionId, reason)
      const session = response.data.session
      set((state) => ({
        currentSession: session,
        sessions: state.sessions.map(s => s.id === sessionId ? session : s),
        loading: false
      }))
    } catch (error: any) {
      console.error('Failed to reject session:', error)
      set({ error: error.response?.data?.error || 'Failed to reject session', loading: false })
    }
  },

  cancelSession: async (sessionId: string) => {
    set({ loading: true, error: null })
    try {
      await multiAgentApi.cancel(sessionId)
      set((state) => ({
        currentSession: state.currentSession?.id === sessionId 
          ? { ...state.currentSession, status: 'cancelled', currentPhase: 'Cancelled' }
          : state.currentSession,
        loading: false,
      }))
    } catch (error: any) {
      console.error('Failed to cancel session:', error)
      set({ error: error.response?.data?.error || 'Failed to cancel session', loading: false })
    }
  },

  deleteSession: async (sessionId: string) => {
    set({ loading: true, error: null })
    try {
      await multiAgentApi.delete(sessionId)
      set((state) => ({
        sessions: state.sessions.filter((s) => s.id !== sessionId),
        currentSession: state.currentSession?.id === sessionId ? null : state.currentSession,
        loading: false,
      }))
    } catch (error: any) {
      console.error('Failed to delete session:', error)
      set({ error: error.response?.data?.error || 'Failed to delete session', loading: false })
    }
  },

  clearCurrentSession: () => {
    set({ currentSession: null, error: null })
  },

  clearError: () => {
    set({ error: null })
  },

  updateSessionFromSocket: (session: MultiAgentSessionData) => {
    set((state) => ({
      currentSession: session,
      sessions: state.sessions.map(s => s.id === session.id ? session : s)
    }))
  },
}))
